<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import PropertyAsyncSelect from "../../../property-async-select"
  import { formatPropertyUnitDisplay } from "../../../../../../../_shared/_logic/unit-labels"

  import {
    findPropertyById,
    formatPropertyAddress,
    normalizeId,
    sanitizePropertyUnitSelection,
    type PropertyUnitSelection,
  } from "../../../../_logic/property-unit-selection"

  type FieldIds = {
    property?: string
    propertyUnit?: string
  }

  type Props = {
    organizationId?: string | null
    properties?: Property[]
    selectedProperty?: Property | null
    showPropertySelect?: boolean
    brightBackground?: boolean
    disabled?: boolean
    fieldIds?: FieldIds
    onChange?: (selection: PropertyUnitSelection) => void
  }

  const defaultFieldIds: Required<FieldIds> = {
    property: "work-order-property",
    propertyUnit: "work-order-property-unit",
  }

  type PublicProps = Props & { value?: PropertyUnitSelection }

  let {
    organizationId = null,
    properties = [],
    selectedProperty = $bindable<Property | null>(null),
    showPropertySelect = true,
    brightBackground = true,
    disabled = false,
    fieldIds = {},
    value = $bindable<PropertyUnitSelection>({ property_id: "", property_unit_id: "" }),
    onChange = undefined,
  }: PublicProps = $props()

  let propertyId = $state(normalizeId(value.property_id))
  let propertyUnitId = $state(normalizeId(value.property_unit_id))

  const resolvedFieldIds = $derived({ ...defaultFieldIds, ...fieldIds })
  const useAsyncPropertySelect = $derived(Boolean(organizationId && showPropertySelect))
  const mergedProperties = $derived.by(() => {
    if (!selectedProperty) return properties

    const existing = findPropertyById(properties, selectedProperty.id)

    return existing ? properties : [...properties, selectedProperty]
  })

  const propertyOptions = $derived(
    mergedProperties.map((property) => ({
      name: formatPropertyAddress(property),
      value: normalizeId(property.id),
    })),
  )

  const resolvedSelectedProperty = $derived.by(
    () =>
      findPropertyById(mergedProperties, propertyId) ??
      (selectedProperty && normalizeId(selectedProperty.id) === normalizeId(propertyId)
        ? selectedProperty
        : null),
  )

  const apartmentOptions = $derived(
    (resolvedSelectedProperty?.units ?? []).map((unit) => ({
      name: formatPropertyUnitDisplay(unit),
      value: normalizeId(unit.id),
    })) ?? [],
  )

  $effect(() => {
    if (selectedProperty !== resolvedSelectedProperty) {
      selectedProperty = resolvedSelectedProperty
    }
  })

  $effect(() => {
    const nextPropertyId = normalizeId(value.property_id)
    const nextPropertyUnitId = normalizeId(value.property_unit_id)

    if (propertyId !== nextPropertyId) {
      propertyId = nextPropertyId
    }

    if (propertyUnitId !== nextPropertyUnitId) {
      propertyUnitId = nextPropertyUnitId
    }
  })

  const applySelection = (next: PropertyUnitSelection, resolvedProperty?: Property | null) => {
    const sanitized = sanitizePropertyUnitSelection(next, mergedProperties, showPropertySelect)
    propertyId = sanitized.property_id
    propertyUnitId = sanitized.property_unit_id
    value = sanitized
    onChange?.(sanitized)
    selectedProperty =
      resolvedProperty === undefined
        ? (findPropertyById(mergedProperties, sanitized.property_id) ?? null)
        : resolvedProperty
  }

  const handlePropertyChange = () => {
    applySelection({ property_id: propertyId, property_unit_id: propertyUnitId })
  }

  const handleAsyncPropertyChange = (property: Property | null) => {
    console.log("Selected property:", property)
    const nextSelection = property
      ? sanitizePropertyUnitSelection(
          {
            property_id: normalizeId(property.id),
            property_unit_id: propertyUnitId,
          },
          [property],
          showPropertySelect,
        )
      : { property_id: "", property_unit_id: "" }
    applySelection(nextSelection, property)
  }

  const handlePropertyUnitChange = () => {
    applySelection({ property_id: propertyId, property_unit_id: propertyUnitId })
  }
</script>

{#if showPropertySelect}
  <section class="row">
    {#if useAsyncPropertySelect && organizationId}
      <PropertyAsyncSelect
        {organizationId}
        id={resolvedFieldIds.property}
        seedProperties={mergedProperties}
        bind:value={propertyId}
        selectedProperty={resolvedSelectedProperty}
        onChange={handleAsyncPropertyChange}
        {brightBackground}
        {disabled}
        placeholder="Liegenschaft wählen"
      />
    {:else}
      <Input
        id={resolvedFieldIds.property}
        type="select"
        options={propertyOptions}
        placeholder="Liegenschaft wählen"
        bind:value={propertyId}
        onChange={handlePropertyChange}
        {brightBackground}
        {disabled}
      />
    {/if}
    <Input
      id={resolvedFieldIds.propertyUnit}
      type="select"
      options={apartmentOptions}
      placeholder="Gebäude- oder Wohneinheit wählen"
      bind:value={propertyUnitId}
      onChange={handlePropertyUnitChange}
      {brightBackground}
      tooltip="Wählen Sie die Gebäude- oder Wohneinheit innerhalb der Liegenschaft aus, für die der Arbeitsauftrag gilt."
      disabled={disabled || !apartmentOptions.length}
    />
  </section>
{/if}

<style lang="less">
  .row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 0.75rem;
  }
</style>
